import { observer } from "mobx-react";
import { Modal } from "react-bootstrap";
import { IModal, StripeRefundStatusEnum } from "../../stores/index.d";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { currencyFormatter, ShowAlert, zeroPad } from "../../utils/functions";
import { useStores } from "../../stores/root-store";
import { IOrder } from "../../stores/order";
import { toNumber } from "lodash";
import { IStripeOrderRefundResponse } from "../../stores/cart";
import { refundRequestKey, refundStorageKey, validRefundAmount } from "../../services/refund-request";

interface IOrderRefund extends IModal {
  order: IOrder;
}

const OrderRefund = observer(({ show, setShowModal, order }: IOrderRefund) => {
  const { orderStore } = useStores();
  const { createRefund, refreshRefundStatus, fetchOrders } = orderStore;

  const [isPartialRefund, setPartialRefund] = useState<boolean>(false);
  const [partialRefundAmount, setPartialRefundAmount] = useState<number>(0);
  const [isFormValid, setIsFormValid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submitLock = useRef(false);
  const activeStorageKey = useRef("");

  useEffect(() => {
    setIsFormValid(!isPartialRefund || validRefundAmount(partialRefundAmount, order.orderTotal));
  }, [isPartialRefund, partialRefundAmount, order.orderTotal]);

  const onRadioChange = (evt: ChangeEvent<HTMLInputElement>) => {
    const { checked } = evt.target;
    setPartialRefund(checked);
  };

  const submitCallback = (
    res: boolean,
    refundResponse: IStripeOrderRefundResponse
  ) => {
    submitLock.current = false;
    setSubmitting(false);
    if (refundResponse?.isFailed) {
      ShowAlert.Error(refundResponse.failedReason);
      return;
    }
    if (res && refundResponse) {
      if (refundResponse.isFailed) {
        ShowAlert.Error(refundResponse.failedReason);
      } else {
        if (refundResponse.status === StripeRefundStatusEnum.Succeeded)
          window.localStorage.removeItem(activeStorageKey.current);
        setShowModal(false);
        ShowAlert.Success(
          `${currencyFormatter(refundResponse.refundAmount)} refund ${refundResponse.status === StripeRefundStatusEnum.Succeeded ? "confirmed" : "submitted, awaiting confirmation"} for Order # ${zeroPad(order.orderId, 8)}`
        );
        if (StripeRefundStatusEnum.Pending == refundResponse.status)
          refreshRefundStatus(order.orderId);
        fetchOrders();
      }
    } else ShowAlert.Error("Refund not confirmed. Check Stripe or retry the same request.");
  };

  const handleFormSubmit = () => {
    if (submitLock.current) return;
    if (!isFormValid)
      ShowAlert.Error("Please make sure all the fields are entered.");
    else {
      submitLock.current = true;
      setSubmitting(true);
      try {
      activeStorageKey.current = refundStorageKey(order.orderId, !isPartialRefund, partialRefundAmount);
      createRefund(
        order.orderId,
        !isPartialRefund,
        isPartialRefund ? partialRefundAmount : 0,
        submitCallback,
        refundRequestKey(activeStorageKey.current)
      );
      } catch {
        submitLock.current = false;
        setSubmitting(false);
        ShowAlert.Error("Unable to save the refund request safely. Check browser storage permissions.");
      }
    }
  };

  return (
    <Modal
      show={show}
      onHide={() => { if (!submitLock.current) setShowModal(false); }}
      centered
      scrollable
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <h4>Order Refund</h4>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form>
          <div className="ps-3">
            <h3>
              <span className="badge text-bg-dark">
                Order # {zeroPad(order.orderId, 8)}
              </span>
            </h3>
            <div className="mb-3">
              Order Total: {currencyFormatter(order.orderTotal)}
            </div>
            <div className="form-check form-switch form-switch-reverse mb-3">
              <label
                className="form-check-label h6"
                htmlFor="allowFutureDateOrders"
              >
                {isPartialRefund ? "Partial refund" : "Full refund"}
              </label>

              <input
                className="form-check-input"
                type="checkbox"
                id="allowFutureDateOrders"
                checked={isPartialRefund}
                disabled={submitting}
                onChange={onRadioChange}
              />
            </div>
            {isPartialRefund && (
              <div className="mb-3">
                <label className="h6">Partial refund amount</label>
                <div className="input-group">
                  <label
                    className="input-group-text bg-secondary text-white"
                    htmlFor="partialRefundAmount"
                  >
                    £
                  </label>
                  <input
                    type="number"
                    min="0.01"
                    max={order.orderTotal}
                    step="0.01"
                    disabled={submitting}
                    className="form-control form-control-lg"
                    id="partialRefundAmount"
                    aria-placeholder="Partial refund"
                    value={partialRefundAmount}
                    onChange={(e) =>
                      setPartialRefundAmount(toNumber(e.target.value))
                    }
                  />
                </div>
              </div>
            )}
            <div className="mb-3">
              {isPartialRefund ? "Requested refund: " : "Full remaining refund (up to): "}
              <strong>
                {currencyFormatter(
                  isPartialRefund ? partialRefundAmount : order.orderTotal
                )}
              </strong>
            </div>
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <button
          className="btn btn-default flex-fill"
          onClick={handleFormSubmit}
          disabled={!isFormValid || submitting}
        >
          <i className="fa fa-check me-2"></i>
          <span>{submitting ? "Submitting…" : "Submit refund"}</span>
        </button>
      </Modal.Footer>
    </Modal>
  );
});

export default OrderRefund;
